/*
 * Project: Goalscape
 * File:    cardUtils.js
 * Path:    src/utils/cardUtils.js
 * Description: Utility functions for card operations and activity tracking.
 * Author:  Akmal Djumakhodjaev
 * Created: [Creation Date - YYYY-MM-DD]
 * Modified: 2025-03-15
 * AI Hint: Consider adding server-side validation for card data to prevent inconsistencies.
 */

/*
 * Function: calculateChecklistProgress
 * Description: Calculates the completion percentage of a checklist.
 * Parameters:
 *   checklist: object - The checklist object containing items array.
 * Returns: number - The percentage of completed items (0-100).
 * Usage:
 *   const progress = calculateChecklistProgress({ items: [{completed: true}, {completed: false}] });
 * AI Hint: Implement caching for checklist progress to reduce calculations.
 */
export const calculateChecklistProgress = (checklist) => {
    if (!checklist.items?.length) return 0;
    const completed = checklist.items.filter(item => item.completed).length;
    return Math.round((completed / checklist.items.length) * 100);
};

/*
 * Function: addActivity
 * Description: Creates an activity log entry for card actions.
 * Parameters:
 *   user: object - The user performing the action.
 *   action: string | object - The action details or action object with type and additional info.
 *   details: string - Optional additional details for the activity (default: '').
 * Returns: object - The activity log entry.
 * Usage:
 *   const activity = addActivity(user, { type: 'move', from: 'Todo', to: 'Done' });
 *   const activity = addActivity(user, 'created the card');
 * AI Hint: Implement real-time activity updates using WebSockets for a more interactive experience.
 */
export const addActivity = (user, action, details = '') => {
    let actionText = '';

    if (typeof action === 'object') {
        const { type, from, to, details: actionDetails } = action;

        switch (type) {
            case 'attachment':
                actionText = `${actionDetails}`;
                break;
            case 'archive':
                actionText = actionDetails;
                break;
            case 'move':
                actionText = `${actionDetails}`;
                break;
            case 'priority':
                if (from === undefined || from === null || from === '') { // Check if 'from' is undefined, null, or empty
                    actionText = `set priority to "${to}"`;
                } else {
                    actionText = `changed priority from "${from}" to "${to}"`;
                }
                break;
            case 'status_change': // New case for status change
                actionText = `moved the card from "${from}" to "${to}"`;
                break;
            case 'assign':
                actionText = actionDetails;
                break;
            case 'unassign': // Handle unassign type
                actionText = actionDetails;
                break;
            case 'title':
                actionText = `changed title from "${from}" to "${to}"`;
                break;
            case 'description':
                actionText = `updated description`;
                break;
            case 'checklist':
                actionText = `${actionDetails} checklist "${from}"`;
                break;
            case 'label':
                actionText = `${actionDetails} label "${from}"`;
                break;
            case 'due_date_set':
                actionText = `${user.displayName} ${action.details}`;
                break;
            case 'due_date_removed':
                actionText = `removed the due date`;
                break;
            case 'start_date_set':
                actionText = `${user.displayName} ${action.details}`;
                break;
            case 'start_date_removed':
                actionText = `removed the start date`;
                break;
            case 'checklist_item_completed':
                actionText = `${actionDetails}`;
                break;
            case 'checklist_item_incomplete': // New case for checklist item incompletion
                actionText = `${actionDetails}`;
                break;
            case 'cover_image': // New case for cover image actions
                actionText = `${actionDetails}`;
                break;
            case 'parent_set':
                actionText = actionDetails; // Details provided by CardModal are descriptive enough
                break;
            case 'parent_removed':
                actionText = actionDetails; // Details provided by CardModal are descriptive enough
                break;
            case 'subtask_created': // New case for subtask creation
                actionText = actionDetails; // Details provided by CardModal are descriptive enough
                break;
            case 'child_task_added': // New case for activity on parent card
                // actionDetails here would be something like "added subtask 'Subtask Title'"
                actionText = actionDetails;
                break;
            // New cases for relationship activities
            case 'manual_relation_added':
            case 'manual_relation_synced': // Assuming same message format desired
            case 'card_blocked_by':
            case 'card_is_blocking':
            case 'card_unblocked_by':
            case 'card_no_longer_blocking':
                actionText = actionDetails; // These types also provide full text in details
                break;
            case 'github_link_added':
                actionText = `added GitHub link: ${actionDetails}`;
                break;
            case 'github_link_updated':
                actionText = `updated GitHub link to: ${actionDetails}`;
                break;
            case 'github_link_removed':
                actionText = `removed GitHub link`;
                break;
            default:
                // If action is an object but type is unknown, try to use details, otherwise stringify.
                // This makes the default slightly more robust for unhandled object actions.
                actionText = actionDetails !== undefined ? actionDetails : String(action);
        }
    } else {
        actionText = action; // Handles plain string actions
    }

    return {
        id: `activity-${Date.now()}`,
        action: actionText,
        timestamp: new Date().toISOString(),
        userId: user.uid,
        userName: user.displayName || user.email,
    };
};

/*
 * Function: duplicateCard
 * Description: Creates a duplicate of a card with a new unique ID while copying all other properties.
 * Parameters:
 *   originalCard: object - The original card object to duplicate.
 * Returns: object - A new card object with a unique ID and copied properties.
 * Usage:
 *   const newCard = duplicateCard(originalCard);
 * AI Hint: Ensure unique ID generation is robust to prevent collisions.
 */
export const duplicateCard = (originalCard) => {
    const newId = `card-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
    const { id, ...rest } = originalCard;
    return {
        ...rest,
        id: newId,
        title: `Copy of ${originalCard.title}`,
        activity: [],
        createdAt: new Date().toISOString()
    };
};
